import { toUint8Array } from "js-base64";
import z from "zod";

export const Disposition = z.enum(["malicious", "spam", "bulk"]);
export type Disposition = z.infer<typeof Disposition>;

interface IEmlSubmission {
  disposition: Disposition;
  clientId: string;
}

const SubmissionErrorBody = z.object({
  errors: z.array(z.object({ message: z.string() })).min(1),
});

/**
 * Reads the first error message from a failed submission response, or
 * undefined when the body is not JSON or does not have the expected shape.
 */
export const readErrorDetail = async (response: Response): Promise<string | undefined> => {
  try {
    const parsed = SubmissionErrorBody.safeParse(await response.json());
    return parsed.success ? parsed.data.errors[0].message : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Fetches the raw EML content of the currently open Outlook item using the
 * Office.js `getAsFileAsync` API (Mailbox Requirement Set 1.9+).
 * Note: getAsFileAsync fully buffers the EML — true client-side streaming is not possible.
 */
const getEMLBase64Content = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!Office.context.requirements.isSetSupported("Mailbox", "1.14")) {
      reject(new Error("EML submission requires Outlook with Mailbox requirement set 1.14 or later."));
      return;
    }

    const item = Office.context.mailbox.item;
    if (!item) {
      reject(new Error("No active mail item"));
      return;
    }
    item.getAsFileAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? "getAsFileAsync failed"));
      }
    });
  });
};

/**
 * Submits the raw EML of the current item to the Worker's `/api/v1/office365/submit` endpoint.
 */
export const submitEML = async ({ disposition, clientId }: IEmlSubmission): Promise<void> => {
  const base64 = await getEMLBase64Content();
  const bytes = toUint8Array(base64);
  const url = new URL(`/api/v1/office365/submit`, window.location.origin);
  url.searchParams.set("disposition", disposition);
  url.searchParams.set("clientId", clientId);

  const recipient = Office.context.mailbox.userProfile.emailAddress;
  url.searchParams.set("recipient", recipient);

  const response = await fetch(url.href, {
    method: "POST",
    headers: { "Content-Type": "message/rfc822" },
    body: bytes,
    signal: AbortSignal.timeout(15000),
  });

  if (response.status === 429) {
    throw new RateLimitError();
  }

  if (!response.ok) {
    switch (response.status) {
      case 404:
        throw new Error("Submission is not yet available. Please try again later.");
      case 413:
        throw new Error("This email is too large to submit.");
      default: {
        const detail = await readErrorDetail(response);
        throw new Error(detail ?? `Submission failed (${response.status}). Please try again.`);
      }
    }
  }
};

export class RateLimitError extends Error {
  constructor() {
    super("Too many requests. Please wait a moment before trying again.");
    this.name = "RateLimitError";
  }
}
